import { FC, useMemo, useState } from 'react';
import { Column } from 'react-table';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { FaFile, FaFileSignature, FaListAlt } from 'react-icons/fa';

import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';

import { useModal } from 'store/modal';
import { useLanguageContext } from 'store/language';
import { useActiveClientContext } from 'store/active-client';
import { usePermissionsContext } from 'store/permissions';
import { PermissionKeys } from 'store/types';

import { formatDate } from 'utils/helpers/formatters';
import { buildClientEntity } from 'utils/helpers/client';
import handlePreviewDoc from 'utils/helpers/handlePreviewDoc';

import { ISelectOptionProps } from 'components/form/types';
import { IАttachment } from 'components/Attachment';
import FetchTable from 'components/table/FetchTable';
import { buildFilters } from 'components/table/utils/filtersConfig';
import TooltipWrapper from 'components/TooltipWrapper';
import Button from 'components/Button';
import Spinner from 'components/Spinner';
import SignDoc from 'components/documents/SignDoc';
import RejectDoc from 'components/documents/RejectDoc';

import InnerWrapper from '../InnerWrapper';

interface IDropdowns {
  status?: ISelectOptionProps[];
  user?: ISelectOptionProps[];
  group?: ISelectOptionProps[];
}

const COMPLETED_STATUS = 'report_signed_by_employer';

const CompletedScreen: FC = () => {
  const { t } = useTranslation();
  const { client } = useActiveClientContext();
  const { language } = useLanguageContext();
  const { showModal } = useModal();
  const { checkPermission } = usePermissionsContext();
  const queryClient = useQueryClient();

  const [loadingPreviewKey, setLoadingPreviewKey] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [customSelectableRowProps, setCustomSelectableRowProps] = useState<string[]>([]);
  const [multiActionActive, setMultiActionActive] = useState(false);

  const {
    isFetching,
    isLoading,
    data: dropdowns,
  } = useQuery<IDropdowns, Error>(
    [buildClientEntity('work-trips-completed-dropdowns', client)],
    async () => {
      const res = await API.get('work-trips/dropdown-options');
      return res.data?.[language || 'bg'];
    },
    { cacheTime: 0 }
  );

  const getTripDays = (fromDate?: string, toDate?: string): number | string => {
    if (!fromDate || !toDate) return '';

    const from = new Date(fromDate);
    const to = new Date(toDate);

    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return '';

    const diffInMs = to.getTime() - from.getTime();
    return Math.floor(diffInMs / (1000 * 60 * 60 * 24)) + 1;
  };

  const getReportAttachments = (rowData: any): IАttachment[] => {
    const normalizeAttachments = (attachments: any[]): IАttachment[] =>
      attachments
        .map(item => ({
          id: item?.id,
          file_name: item?.file_name || item?.name || `#${item?.id}`,
          extension: item?.extension || item?.file_extension || '',
        }))
        .filter(item => item.id);

    if (Array.isArray(rowData.attachments)) {
      return normalizeAttachments(rowData.attachments);
    }

    if (Array.isArray(rowData.report_documents)) {
      return normalizeAttachments(rowData.report_documents);
    }

    if (Array.isArray(rowData.report_files)) {
      return normalizeAttachments(rowData.report_files);
    }

    if (Array.isArray(rowData.report_attachment_document_ids)) {
      return rowData.report_attachment_document_ids
        .filter(Boolean)
        .map((id: number) => ({ id, file_name: `#${id}`, extension: '' }));
    }

    return [];
  };

  const handleAttachmentClick = (id: number) => {
    return API.get(`work-trips/attachment/${id}/download`)
      .then(res => {
        window.open(res.data, '_blank');
      })
      .catch(err => {
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        });
      });
  };

  const handleAttachmentsModal = (attachments: IАttachment[], heading?: string) => {
    const links = attachments.map(attachment => ({
      id: attachment.id,
      fileName: attachment.extension ? `${attachment.file_name}.${attachment.extension}` : attachment.file_name,
    }));

    showModal({
      type: 'list',
      heading: heading || t('workTrips.previewReportDocuments'),
      onLinkClick: handleAttachmentClick,
      links,
    });
  };

  const getDocIdFromActionLink = (actionLink?: string): number | null => {
    if (!actionLink) return null;

    const match = actionLink.match(/\/documents\/(\d+)/);
    if (!match?.[1]) return null;

    return Number(match[1]);
  };

  const handlePreviewClick = (docId: number, key: string) => {
    setLoadingPreviewKey(key);
    handlePreviewDoc({
      showModal,
      id: docId,
      setIsLoading: setIsLoadingPreview,
    });
  };

  const renderActions = (buttons: any[] = [], rowData: any) => {
    const reportAttachments = getReportAttachments(rowData);
    const previewOrderButton = buttons.find(button => button.type === 'order_preview');
    const previewReportButton = buttons.find(button => button.type === 'report_preview');
    const attachmentsButton = buttons.find(button => button.type === 'attachments');
    const previewOrderDocId = rowData.order_document_id || getDocIdFromActionLink(previewOrderButton?.action_link);
    const previewReportDocId = rowData.report_document_id || getDocIdFromActionLink(previewReportButton?.action_link);
    const orderPreviewKey = `${rowData.id}-order-preview`;
    const reportPreviewKey = `${rowData.id}-report-preview`;

    return (
      <>
        {buttons.map((button, index) => {
          const buttonDocumentId =
            getDocIdFromActionLink(button?.action_link) || rowData.order_document_id || rowData.report_document_id;

          if (button.type === 'sign' && buttonDocumentId) {
            return (
              <SignDoc
                key={`${buttonDocumentId}-sign-completed-${index}`}
                id={String(buttonDocumentId)}
                docHolderId={String(buttonDocumentId)}
                buttonText={button.text || t('documents.table.sign')}
                handlePreview={() => handlePreviewDoc({ showModal, id: buttonDocumentId })}
                invalidate={() => queryClient.invalidateQueries(buildClientEntity('work-trips-completed', client))}
                size="sm"
              />
            );
          }

          if (button.type === 'reject' && buttonDocumentId) {
            return (
              <RejectDoc
                ariaLabel="Reject document"
                key={`${buttonDocumentId}-reject-completed-${index}`}
                id={String(buttonDocumentId)}
                docHolderId={String(buttonDocumentId)}
                buttonText={button.text || t('documents.table.reject')}
                handlePreview={() => handlePreviewDoc({ showModal, id: buttonDocumentId })}
                invalidate={() => queryClient.invalidateQueries(buildClientEntity('work-trips-completed', client))}
                size="sm"
                reason={false}
              />
            );
          }

          return null;
        })}

        {(previewOrderButton || rowData.order_document_id) && !!previewOrderDocId && (
          <span className="d-inline-block ml-2 mb-1">
            <TooltipWrapper
              id={`tooltip-order-completed-${rowData.id}`}
              overlay={previewOrderButton?.text || t('workTrips.previewOrder')}
            >
              <Button
                type="button"
                btnType="primary"
                size="sm"
                disabled={loadingPreviewKey === orderPreviewKey && isLoadingPreview}
                onClick={() => handlePreviewClick(previewOrderDocId, orderPreviewKey)}
              >
                {!(loadingPreviewKey === orderPreviewKey && isLoadingPreview) && <FaFileSignature size={14} />}
                {loadingPreviewKey === orderPreviewKey && isLoadingPreview && <Spinner size={14} />}
              </Button>
            </TooltipWrapper>
          </span>
        )}

        {(previewReportButton || rowData.report_document_id) && !!previewReportDocId && (
          <span className="d-inline-block ml-2 mb-1">
            <TooltipWrapper
              id={`tooltip-report-completed-${rowData.id}`}
              overlay={previewReportButton?.text || t('workTrips.previewReport')}
            >
              <Button
                type="button"
                btnType="secondary"
                size="sm"
                disabled={loadingPreviewKey === reportPreviewKey && isLoadingPreview}
                onClick={() => handlePreviewClick(previewReportDocId, reportPreviewKey)}
              >
                {!(loadingPreviewKey === reportPreviewKey && isLoadingPreview) && <FaFile size={14} />}
                {loadingPreviewKey === reportPreviewKey && isLoadingPreview && <Spinner size={14} />}
              </Button>
            </TooltipWrapper>
          </span>
        )}

        {(attachmentsButton || reportAttachments.length > 0) && reportAttachments.length > 0 && (
          <span className="d-inline-block ml-2 mb-1">
            <TooltipWrapper
              id={`tooltip-report-docs-completed-${rowData.id}`}
              overlay={attachmentsButton?.text || t('workTrips.previewReportDocuments')}
            >
              <Button
                type="button"
                btnType="secondary"
                size="sm"
                onClick={() => handleAttachmentsModal(reportAttachments, attachmentsButton?.text)}
              >
                <FaListAlt size={14} />
              </Button>
            </TooltipWrapper>
          </span>
        )}
      </>
    );
  };

  const columns: Array<Column<any>> = [
    {
      Header: t('workTrips.user'),
      accessor: 'user',
      Cell: ({ row }) => {
        const { user } = row.original;
        return <>{user?.display_name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim()}</>;
      },
    },
    {
      Header: t('workTrips.group'),
      accessor: 'user.groups',
      Cell: ({ row }) => (
        <>
          {row.original.user?.groups?.length ? row.original.user.groups.map((group: any) => group.name).join(', ') : ''}
        </>
      ),
    },
    {
      Header: t('workTrips.destination'),
      accessor: 'destination',
      Cell: ({ row }) =>
        row.original.route ? (
          <TooltipWrapper
            id={`tooltip-work-trips-route-${row.original.id}`}
            overlay={`${t('workTrips.route')}: ${row.original.route}`}
          >
            <>{row.original.displayed_destination || row.original.destination}</>
          </TooltipWrapper>
        ) : (
          <>{row.original.displayed_destination || row.original.destination}</>
        ),
    },
    {
      Header: t('workTrips.period'),
      accessor: 'from_date',
      Cell: ({ row }) => (
        <>
          {row.original.from_date ? formatDate(new Date(row.original.from_date), 'dd.mm.yyy') : ''} -{' '}
          {row.original.to_date ? formatDate(new Date(row.original.to_date), 'dd.mm.yyy') : ''}
        </>
      ),
    },
    {
      Header: t('workTrips.daysCount'),
      accessor: 'days_count',
      Cell: ({ row }) => <>{row.original.days_count || getTripDays(row.original.from_date, row.original.to_date)}</>,
    },
    {
      Header: t('workTrips.totalCost'),
      accessor: 'total_cost',
      Cell: ({ row }) => (
        <>{row.original.total_cost ?? row.original.total_expenses ?? row.original.daily_allowance ?? '-'}</>
      ),
    },
    {
      Header: t('workTrips.status'),
      accessor: 'status',
      Cell: ({ row }) => <>{t(`workTrips.statuses.${row.original.status}`)}</>,
    },
    {
      Header: () => <div className="text-right">{t('workTrips.actions')}</div>,
      accessor: 'actions',
      width: '14%',
      Cell: ({ row }) => (
        <div style={{ textAlign: 'right' }}>{renderActions(row.original.buttons || [], row.original)}</div>
      ),
    },
  ];

  const tableFilters = useMemo(() => {
    if (!dropdowns) return null;

    const statusWithDefaults = dropdowns.status?.map(opt => {
      if (String(opt.value) === COMPLETED_STATUS) {
        return { ...opt, selected: true };
      }
      return opt;
    });

    return [
      buildFilters({
        name: 'from_date_gte',
        placeholder: t('leaves.dateFrom'),
        type: 'monthyearpickerstart',
      }),
      buildFilters({
        name: 'to_date_lte',
        placeholder: t('leaves.dateTo'),
        type: 'monthyearpickerend',
      }),
      buildFilters({
        name: 'user',
        placeholder: t('workTrips.user'),
        options: dropdowns.user,
        isMulti: true,
        limitOptions: true,
        showTotal: true,
        allowSelectAll: true,
      }),
      buildFilters({
        name: 'group',
        placeholder: t('workTrips.group'),
        options: dropdowns.group,
        isMulti: true,
        showTotal: true,
        allowSelectAll: true,
      }),
      buildFilters({
        name: 'status',
        placeholder: t('workTrips.status'),
        options: statusWithDefaults,
        isMulti: true,
        showTotal: true,
        allowSelectAll: true,
      }),
    ];
  }, [dropdowns, t]);

  return (
    <InnerWrapper
      canMultiSign={checkPermission(PermissionKeys.CAN_MULTI_SIGN)}
      canMultiDownload
      customSelectableProps={customSelectableRowProps}
      setCustomSelectableRowProps={setCustomSelectableRowProps}
      setMultiActionActive={setMultiActionActive}
    >
      {tableFilters && (
        <FetchTable
          loading={isFetching || isLoading}
          entityName={buildClientEntity('work-trips-completed', client)}
          entityEndpoint="/work-trips"
          columns={columns}
          filters={tableFilters}
          pageLimit={20}
          pagination
          hideSearch
          isSelectable
          useCustomSelectBtn
          customSelectableRowProps={customSelectableRowProps}
          disablePagination={multiActionActive}
        />
      )}
    </InnerWrapper>
  );
};

export default CompletedScreen;
